import React from 'react';

import { css } from '@emotion/react';
import { DateOfBirthInputFormatter, SocialSecurityInputFormatter } from '@x1/util-format';
import moment from 'moment';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import LoadingPage from './LoadingPage';
import logger from 'src/clients/LoggingClient';
import { IDVFailPageMutation as IDVFailPageMutationType } from 'src/clients/Network/gql/build/IDVFailPageMutation.graphql';
import { IDVFailPageQuery as IDVFailPageQueryType } from 'src/clients/Network/gql/build/IDVFailPageQuery.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import GetHelpTextButton from 'src/components/creditApplication/GetHelpTextButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';

const IDVFailPageQuery = graphql`
  query IDVFailPageQuery {
    authIdentity {
      id
      settings {
        id
        name {
          id
          firstName
          lastName
        }
        birthday {
          id
          bornAt
        }
      }
    }
    identityEditability(q: { flowType: CREDIT_APPLICATION_IDV_CHALLENGE }) {
      firstName {
        editability
      }
      lastName {
        editability
      }
      ssn {
        editability
      }
      dob {
        editability
      }
    }
  }
`;

const IDVFailPageMutation = graphql`
  mutation IDVFailPageMutation(
    $identityId: String!
    $creditApplicationId: String!
    $name: UpdateIdentityName
    $bornAt: Long
    $ssn: String
  ) {
    updateIdentity(
      q: { identityId: $identityId, creditApplicationId: $creditApplicationId, name: $name, bornAt: $bornAt, ssn: $ssn }
    ) {
      data {
        id
      }
      errors
    }
  }
`;

const IDVFailPage = () => {
  const {
    authIdentity: { settings },
    identityEditability,
  } = useLazyLoadQuery<IDVFailPageQueryType>(IDVFailPageQuery, {}, { fetchPolicy: 'network-only' });
  const identityId = useGlobalStore((s) => s.identityId);
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const { underwrite, underwriteLoading } = React.useContext(NavigationContext);
  const [updateCommit, updateLoading] = useAsyncMutation<IDVFailPageMutationType>(IDVFailPageMutation);
  const [firstName, setFirstName] = React.useState<string>(settings?.name?.firstName ?? '');
  const [lastName, setLastName] = React.useState<string>(settings?.name?.lastName ?? '');
  const [birthdate, setBirthdate] = React.useState<string>(
    settings?.birthday?.bornAt ? moment(settings.birthday.bornAt).format('MM/DD/YYYY') : '',
  );
  const [ssn, setSSN] = React.useState<string>('');
  const loading = updateLoading || underwriteLoading;

  const onChangeFirstName = React.useCallback(({ value }: { value: string }) => {
    setFirstName(value);
  }, []);

  const onChangeLastName = React.useCallback(({ value }: { value: string }) => {
    setLastName(value);
  }, []);

  const onChangeBirthdate = React.useCallback(({ value }: { value: string }) => {
    const formatted = DateOfBirthInputFormatter.serialize(value);
    setBirthdate(formatted);
  }, []);

  const onChangeSSN = React.useCallback(
    ({ value }: { value: string }) => {
      const formattedValue = SocialSecurityInputFormatter.serialize(value);
      setSSN(formattedValue);
    },
    [setSSN],
  );

  const buttonState = React.useMemo(() => {
    if (loading) return 'loading';
    const unformattedBday = DateOfBirthInputFormatter.deserialize(birthdate);
    return (!identityEditability.firstName.editability || firstName.length > 0) &&
      (!identityEditability.lastName.editability || lastName.length > 0) &&
      (!identityEditability.dob.editability ||
        (moment(unformattedBday, 'MMDDYYYY', true).isValid() &&
          moment(unformattedBday, 'MMDDYYYY').isBefore(moment()))) &&
      (!identityEditability.ssn.editability || SocialSecurityInputFormatter.deserialize(ssn).length === 9)
      ? 'default'
      : 'disabled';
  }, [identityEditability, firstName, lastName, birthdate, ssn, loading]);

  const onConfirm = React.useCallback(async () => {
    try {
      if (!!identityId && !!creditApplicationId) {
        const res = await updateCommit({
          variables: {
            identityId,
            creditApplicationId,
            name:
              identityEditability.firstName.editability && identityEditability.lastName.editability
                ? { firstName, lastName }
                : undefined,
            bornAt: identityEditability.dob.editability ? moment(birthdate, 'MM/DD/YYYY').valueOf() : undefined,
            ssn: identityEditability.ssn.editability ? ssn : undefined,
          },
        });
        if (res.updateIdentity.errors.length > 0) {
          showGlobalErrorAlert();
        } else {
          await underwrite();
        }
      } else {
        showGlobalErrorAlert();
      }
    } catch (e) {
      logger.error(e, { source: 'IDVFailPage.onConfirm' });
      showGlobalErrorAlert();
    }
  }, [
    updateCommit,
    identityEditability,
    firstName,
    lastName,
    birthdate,
    ssn,
    identityId,
    creditApplicationId,
    underwrite,
  ]);

  return (
    <CreditApplicationPageLayout
      title='Confirm your information'
      subtitle='We weren’t able to match the records below. Please edit them below for errors or confirm.'
      showLogo={false}
    >
      <div css={root}>
        {identityEditability.firstName.editability && (
          <TextInput
            placeholder='First name'
            value={firstName}
            onChange={onChangeFirstName}
            autoComplete='given-name'
          />
        )}
        {identityEditability.lastName.editability && (
          <TextInput placeholder='Last name' value={lastName} onChange={onChangeLastName} autoComplete='family-name' />
        )}
        {identityEditability.dob.editability && (
          <TextInput
            placeholder='Birth date'
            value={birthdate}
            onChange={onChangeBirthdate}
            autoComplete='bday'
            inputMode='numeric'
          />
        )}
        {identityEditability.ssn.editability && (
          <TextInput placeholder='Social Security Number' value={ssn} onChange={onChangeSSN} inputMode='numeric' />
        )}
        <div css={footer}>
          <Button label='Confirm' state={buttonState} width='full' type='submit' onClick={onConfirm} />
          <GetHelpTextButton />
        </div>
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
`;

const footer = css`
  margin-top: 40px;
  gap: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WithSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <IDVFailPage />
    </React.Suspense>
  );
};

export default WithSuspense;
